import { RootStore } from 'store/rootStore';
import { makeAutoObservable, runInAction } from 'mobx';
import { EnrollmentPeriod, Proposal, RfqProposalStatus } from 'types/enrollmentService';
import { ProductLineId } from '@insurancemenu/imenu-components-lib/dist/types/ProductLineId';
import { PRODUCT_LINES_CONTENT_MAP } from '@insurancemenu/imenu-components-lib/dist/utils/ProductLinesHelper';
import { getAllEnrollmentPeriodsByGroupId, markAsPreferred } from 'api/operations/enrollmentService';
import { getProposalDownloadURL } from 'api/operations/proposalServiceV3';
import { ProposalFileType } from 'types/proposalService';

export interface GroupProposalPerPeriod {
  proposal: Proposal;
  enrollmentPeriod: EnrollmentPeriod;
}

interface CurrentProposal {
  enrollmentPeriodId: string;
  cartId: string;
}

const INITIAL_CURRENT_PROPOSAL: Readonly<CurrentProposal> = Object.freeze({
  enrollmentPeriodId: '',
  cartId: '',
});

type ConfirmationModalType = 'DELETE_PROPOSAL' | 'MARK_AS_PREFERRED';

export class GroupProposalsTableStore {
  rootStore: RootStore;
  groupId = '';
  groupProposalsPerPeriod: GroupProposalPerPeriod[] = [];
  proposalPdfUrls: Record<string, string> = {};
  loading = false;
  currentProposal: CurrentProposal = {
    ...INITIAL_CURRENT_PROPOSAL,
  };

  allEnrollmentPeriods: EnrollmentPeriod[] = [];
  showPlanDetailsModal = false;
  showProductDetailsModal = false;

  // Confirmation Modal
  showConfirmationModal = false;
  confirmationModalType: ConfirmationModalType | null = null;

  public openConfirmationModal(type: ConfirmationModalType, currentProposal: CurrentProposal): void {
    this.showConfirmationModal = true;
    this.confirmationModalType = type;
    this.currentProposal = currentProposal;
  }

  public closeConfirmationModal(): void {
    this.showConfirmationModal = false;
    this.confirmationModalType = null;
    this.currentProposal = { ...INITIAL_CURRENT_PROPOSAL };
  }

  public buildProposalsList(groupId: string): void {
    this.groupId = groupId;
    this.groupProposalsPerPeriod = [];
    this.allEnrollmentPeriods.forEach((enrollmentPeriod) => {
      enrollmentPeriod.proposals.forEach((proposal) => {
        this.groupProposalsPerPeriod.push({
          proposal: {
            ...proposal,
            productLines: proposal.productLines.filter(
              (productLine) => PRODUCT_LINES_CONTENT_MAP[productLine.id]?.rateSegment === proposal.rateSegment,
            ),
          },
          enrollmentPeriod,
        });
      });
    });
    this.groupProposalsPerPeriod.sort((a, b) => b.proposal.name.localeCompare(a.proposal.name));
  }

  public async updateProposalList(groupId: string): Promise<void> {
    this.loading = true;
    const result = await getAllEnrollmentPeriodsByGroupId(groupId);
    const releasedCartIds = runInAction(() => {
      this.proposalPdfUrls = {};
      if (result) {
        this.allEnrollmentPeriods = result;
        this.buildProposalsList(groupId);
      }
      return this.groupProposalsPerPeriod
        .filter(({ proposal }) => proposal.status === RfqProposalStatus.RELEASED)
        .map(({ proposal }) => proposal.cartId);
    });

    const urls = await Promise.all(
      releasedCartIds.map(async (cartId) => ({
        cartId,
        url: await getProposalDownloadURL(cartId, ProposalFileType.PDF),
      })),
    );

    runInAction(() => {
      this.proposalPdfUrls = Object.fromEntries(urls.filter(({ url }) => url).map(({ cartId, url }) => [cartId, url]));
      this.loading = false;
    });
  }

  public async deleteProposal(enrollmentPeriodId: string, cartId: string): Promise<void> {
    this.loading = true;
    await this.rootStore.enrollmentStore.deleteProposal(enrollmentPeriodId, cartId);
    runInAction(() => {
      this.groupProposalsPerPeriod = this.groupProposalsPerPeriod.filter(
        (groupProposalPerPeriod) => groupProposalPerPeriod.proposal.cartId !== cartId,
      );
      this.loading = false;
    });
  }

  public async markAsPreferred(enrollmentPeriodId: string, cartId: string): Promise<void> {
    this.loading = true;
    await markAsPreferred(enrollmentPeriodId, cartId);
    runInAction(() => {
      void this.updateProposalList(this.groupId);
      this.loading = false;
    });
  }

  public v2ProposalRedirect(productLineId: ProductLineId, cartId: string): string {
    return `/browsing?groupId=${this.groupId}&productLineId=${productLineId}&cartId=${cartId}`;
  }

  public setShowPlanDetailsModal(value: boolean): void {
    this.showPlanDetailsModal = value;
  }

  public setShowProductDetailsModal(value: boolean): void {
    this.showProductDetailsModal = value;
  }

  public getPeriodsLocally(periods: EnrollmentPeriod[]): void {
    this.allEnrollmentPeriods = periods;
  }

  public setLoading(loading: boolean): void {
    this.loading = loading;
  }

  public resetProposalsData(): void {
    this.groupProposalsPerPeriod = [];
    this.proposalPdfUrls = {};
    this.rootStore.enrollmentStore.setEnrollmentPeriods([]);
  }

  constructor(rootStore: RootStore) {
    this.rootStore = rootStore;

    makeAutoObservable(this, {}, { autoBind: true });
  }
}
